// Canonical seat-configuration math, shared by every page that renders a bus/
// vehicle seat diagram (FleetPage, TripsPage, PassengerSearchPage, PassengerBookPage).
// Mirrors backend/src/utils/seatLayout.js's PER_ROW_BY_CONFIG numbers exactly —
// if this table ever changes, update both.
//
// This is the back-row-aware algorithm originally written for PassengerBookPage's
// SeatDiagram — the single source of truth going forward. TripsPage's older
// generateAdminBusRows() inferred the back row purely from a short last chunk
// (ignoring backRowSize); switching it to this util is an intentional
// correctness fix for buses with a real back bench.
export const SEAT_CONFIGS = {
  '2+2': { left: 2, right: 2, label: 'قياسي' },
  '2+1': { left: 2, right: 1, label: 'VIP' },
  '1+1': { left: 1, right: 1, label: 'ميني' },
  'van': { left: 2, right: 1, label: 'فان' },
};

export const getBusLayoutCfg = (bus) => SEAT_CONFIGS[bus?.seatLayout?.config] || SEAT_CONFIGS['2+2'];

// Fixed physical layout for حافلة صغيرة (van, 11-15 seats) — not a variant of
// the generic left/right row loop below. Row 2 has no right-side seat (door),
// and the back bench scales with capacity: front section is always exactly
// 11 seats (1-11), any seats beyond that fill the back bench (0-4 seats, 12-15).
const VAN_FRONT_ROWS = [
  { left: [1, 2],   right: [3]  },
  { left: [4, 5],   right: [],  rightBlocked: true },
  { left: [6, 7],   right: [8]  },
  { left: [9, 10],  right: [11] },
];

const generateVanLayout = (totalSeats) => {
  const cfg = SEAT_CONFIGS['van'];
  const backRowSize = Math.max(0, Math.min(4, totalSeats - 11));
  const rows = VAN_FRONT_ROWS.map(r => ({ ...r, flat: [...r.left, ...r.right] }));
  const backSeats = Array.from({ length: backRowSize }, (_, i) => 12 + i);

  return {
    cfg,
    seatsPerRow: cfg.left + cfg.right,
    backRowSize,
    hasBackRow: backRowSize > 0,
    mainSeatCount: 11,
    rows,
    backSeats,
  };
};

// Builds the full row/back-bench breakdown for a bus.
//   totalSeats — authoritative seat count (bus.totalSeats, with a caller-supplied fallback)
//   bus        — { seatLayout: { config, backRowSize } } (or undefined → defaults)
//
// Returns:
//   cfg           — { left, right, label }
//   seatsPerRow   — cfg.left + cfg.right
//   backRowSize   — effective back-bench size (0 if none)
//   hasBackRow    — whether a back bench exists for this seat count
//   mainSeatCount — seats allocated to regular rows (excludes the back bench)
//   rows          — [{ left: number[], right: number[], flat: number[], rightBlocked?, leftBlocked? }]
//                   one entry per main row; *Blocked flags mark a deliberately empty
//                   seat slot (e.g. the van's door row) so renderers can show a
//                   placeholder instead of just collapsing the space.
//   backSeats     — number[] — the back-bench seat numbers (empty if hasBackRow is false)
export const generateVehicleLayout = (totalSeats, bus) => {
  if (bus?.seatLayout?.config === 'van') return generateVanLayout(totalSeats);

  const cfg = getBusLayoutCfg(bus);
  const backRowSize = bus?.seatLayout?.backRowSize ?? 5;
  const hasBackRow = backRowSize > 0 && totalSeats > backRowSize;
  const mainSeatCount = hasBackRow ? totalSeats - backRowSize : totalSeats;

  const rows = [];
  let seat = 1;
  while (seat <= mainSeatCount) {
    const left = Array.from({ length: cfg.left }, () => (seat <= mainSeatCount ? seat++ : null)).filter(Boolean);
    const right = Array.from({ length: cfg.right }, () => (seat <= mainSeatCount ? seat++ : null)).filter(Boolean);
    if (left.length || right.length) rows.push({ left, right, flat: [...left, ...right] });
  }

  const backSeats = hasBackRow
    ? Array.from({ length: backRowSize }, (_, i) => mainSeatCount + 1 + i)
    : [];

  return {
    cfg,
    seatsPerRow: cfg.left + cfg.right,
    backRowSize: hasBackRow ? backRowSize : 0,
    hasBackRow,
    mainSeatCount,
    rows,
    backSeats,
  };
};
